import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const fmt = (n) => `₹${(n || 0).toLocaleString()}`;

export const FinanceAnalytics = () => {
  const { data: a } = useQuery({ queryKey: ['fin-analytics'], queryFn: () => api.get('/finance/analytics').then((r) => r.data) });
  if (!a) return null;
  return (
    <div className="mb-8" data-testid="finance-analytics">
      <h2 className="font-display text-lg font-semibold mb-3">Analytics</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
        {[['invoiced', 'Invoiced'], ['collected', 'Collected'], ['outstanding', 'Outstanding'], ['concessions', 'Concessions'], ['credit_notes', 'Credit notes'], ['written_off', 'Written off']].map(([k, label]) => (
          <div key={k} className="bg-white border border-slate-200 rounded-md p-4">
            <p className="font-mono-label text-[10px] uppercase text-slate-400">{label}</p>
            <p className={`font-display text-xl font-extrabold ${k === 'collected' ? 'text-green-700' : k === 'outstanding' ? 'text-rose-600' : ''}`} data-testid={`analytics-${k}`}>{fmt(a.totals[k])}</p>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="font-mono-label text-[10px] uppercase text-slate-400">Monthly collections</p>
            <span className="font-mono-label text-[10px] uppercase px-2 py-0.5 rounded bg-green-100 text-green-800" data-testid="collection-rate">Collection rate {a.totals.collection_rate}%</span>
          </div>
          {a.monthly_collections.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={a.monthly_collections}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Bar dataKey="amount" fill="#0f172a" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 py-8 text-center">No payments recorded yet.</p>}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Revenue by grade</p>
            <div className="space-y-1.5">
              {a.by_grade.map((g) => (
                <div key={g.grade} className="text-xs">
                  <div className="flex justify-between"><span>{g.grade}</span><span className="font-semibold">{fmt(g.collected)} / {fmt(g.invoiced)}</span></div>
                  <div className="h-1.5 bg-slate-100 rounded mt-0.5"><div className="h-1.5 bg-slate-900 rounded" style={{ width: `${g.invoiced ? Math.min(100, (g.collected / g.invoiced) * 100) : 0}%` }} /></div>
                </div>
              ))}
              {a.by_grade.length === 0 && <p className="text-xs text-slate-400">No data</p>}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">By category / status</p>
            <div className="space-y-1 text-xs">
              {a.by_category.map((c) => (
                <div key={c.category} className="flex justify-between"><span className="capitalize">{c.category.replace('_', ' ')}</span><span className="font-semibold">{fmt(c.collected)}</span></div>
              ))}
              <div className="border-t border-slate-100 pt-1 mt-1">
                {Object.entries(a.status_distribution).map(([s, n]) => (
                  <div key={s} className="flex justify-between"><span className="capitalize">{s.replace('_', ' ')}</span><span>{n}</span></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
